import React, { useCallback, useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import { Button } from '../Button';
import { Icon } from '../Icon';
import type {
  DataContainerCardProps,
  DataContainerCardBorderRadius,
  DataContainerCardShadow,
  DataContainerCardContentPadding,
} from './DataContainerCard.types';

/**
 * Maps border radius prop to CSS value
 */
const getBorderRadius = (radius: DataContainerCardBorderRadius, theme: Record<string, unknown>): string => {
  const themeRadius = theme.borders as { radius: { sm: string; md: string; lg: string } };
  switch (radius) {
    case 'none':
      return '0';
    case 'sm':
      return themeRadius.radius.sm;
    case 'md':
      return themeRadius.radius.md;
    case 'lg':
      return '12px';
    default:
      return '0';
  }
};

/**
 * Maps shadow prop to CSS value
 */
const getShadow = (shadow: DataContainerCardShadow): string => {
  switch (shadow) {
    case 'none':
      return 'none';
    case 'sm':
      return '0 1px 3px rgba(0, 0, 0, 0.08)';
    case 'md':
      return '0 2px 8px rgba(0, 0, 0, 0.12)';
    case 'lg':
      return '0 4px 16px rgba(0, 0, 0, 0.16)';
    default:
      return 'none';
  }
};

/**
 * Maps content padding prop to CSS value
 */
const getContentPadding = (padding: DataContainerCardContentPadding, isChild: boolean): string => {
  if (isChild) {
    switch (padding) {
      case 'none':
        return '0';
      case 'sm':
        return '8px 8px 8px 8px';
      case 'md':
        return '12px 10px 10px 10px';
      default:
        return '12px 10px 10px 10px';
    }
  }
  switch (padding) {
    case 'none':
      return '0';
    case 'sm':
      return '8px 8px 8px 8px';
    case 'md':
      return '16px 12px 12px 12px';
    default:
      return '16px 12px 12px 12px';
  }
};

interface ContainerStyleProps {
  $borderRadius: DataContainerCardBorderRadius;
  $shadow: DataContainerCardShadow;
  $showBorder?: boolean;
  $borderColor?: string;
  $isChild?: boolean;
  $isOpen?: boolean;
  $collapsible?: boolean;
}

/**
 * Main container for the DataContainerCard component
 */
const Container = styled.div<ContainerStyleProps>`
  ${({ theme, $borderRadius, $shadow, $showBorder, $borderColor, $isChild, $isOpen, $collapsible }) => {
    const radius = getBorderRadius($borderRadius, theme as unknown as Record<string, unknown>);
    const shadow = getShadow($shadow);
    // For non-collapsible cards or when open, allow overflow for dropdowns
    // Keep overflow hidden only during collapse animation
    const overflow = !$collapsible ? 'visible' : ($isOpen ? 'visible' : 'hidden');

    return css`
      border-radius: ${radius};
      box-shadow: ${shadow};
      overflow: ${overflow};
      background-color: ${theme.colors.white};
      border: 1px solid ${$showBorder ? ($borderColor || theme.colors.border.light) : 'transparent'};

      &:focus {
        border-color: ${theme.colors.border.light};
      }

      ${$isChild &&
      css`
        &:not(:last-child) {
          margin-bottom: 12px;
        }
      `}
    `;
  }}
`;

interface HeaderStyleProps {
  $isOpen: boolean;
  $height?: number;
  $isChild?: boolean;
  $showHeaderBorderBottom?: boolean;
  $stickyHeader?: boolean;
  $collapsible?: boolean;
}

/**
 * Header component of the DataContainerCard
 */
const Header = styled.div<HeaderStyleProps>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${({ $isChild }) => ($isChild ? '0 12px 0 10px' : '0 16px 0 12px')};
  cursor: ${({ $collapsible }) => ($collapsible ? 'pointer' : 'default')};
  background-color: ${({ theme }) => theme.colors.background.default};
  height: ${({ $height, $isChild }) => {
    if ($height === 0) return 'unset';
    if ($height !== undefined) return `${$height}px`;
    return $isChild ? '32px' : '36px';
  }};
  user-select: none;
  transition: background-color 0.3s ease;
  border-bottom: ${({ $isOpen, $showHeaderBorderBottom, theme }) =>
    $showHeaderBorderBottom && $isOpen ? `1px solid ${theme.colors.border.light}` : '1px solid transparent'};
  gap: 8px;

  ${({ $stickyHeader }) =>
    $stickyHeader &&
    css`
      position: sticky;
      top: 0;
      z-index: 1;
    `}

  ${({ $collapsible, theme }) =>
    $collapsible &&
    css`
      &:hover {
        background-color: ${theme.colors.grey[100]};
      }
    `}
`;

interface ContentStyleProps {
  $isOpen: boolean;
  $isChild: boolean;
  $hasContent: boolean;
  $contentPadding: DataContainerCardContentPadding;
  $collapsible: boolean;
}

/**
 * Content area of the DataContainerCard
 */
const Content = styled.div<ContentStyleProps>`
  transition:
    max-height 0.3s cubic-bezier(0.4, 0, 0.2, 1),
    opacity 0.3s cubic-bezier(0.4, 0, 0.2, 1),
    padding 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  padding: ${({ $isOpen, $hasContent, $contentPadding, $isChild }) =>
    $isOpen && $hasContent ? getContentPadding($contentPadding, $isChild) : '0 12px'};
  opacity: ${({ $isOpen, $hasContent }) => ($isOpen && $hasContent ? 1 : 0)};
  background-color: ${({ $isChild, theme }) => ($isChild ? theme.colors.grey[100] : theme.colors.white)};
`;

/**
 * Styled component for the nested header title (smaller)
 */
const NestedHeaderTitle = styled.h3`
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  font-family: ${({ theme }) => theme.typography.fontFamily};
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: ${({ theme }) => theme.colors.text.primary};
`;

/**
 * Styled component for the header title
 */
const HeaderTitle = styled.h2`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  font-family: ${({ theme }) => theme.typography.fontFamily};
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: ${({ theme }) => theme.colors.text.primary};
`;

/**
 * Left section of the header
 */
const LeftSection = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  flex-grow: 1;

  > svg {
    flex-shrink: 0;
  }
`;

/**
 * Right section of the header
 */
const RightSection = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
`;

/**
 * DataContainerCard is a generic container component for displaying data sections
 * with consistent styling. It supports both collapsible (accordion) and static
 * display modes, nested containers, and customizable visual styling.
 *
 * @example
 * ```tsx
 * // Basic non-collapsible usage
 * <DataContainerCard title="Review Details" collapsible={false}>
 *   <MetadataDetails name="Status" value="Complete" />
 * </DataContainerCard>
 *
 * // Collapsible usage
 * <DataContainerCard title="Select Files" collapsible defaultOpen>
 *   <FileInputSection />
 * </DataContainerCard>
 *
 * // Nested containers
 * <DataContainerCard title="Review Results" collapsible defaultOpen>
 *   <DataContainerCard title="High Severity" isChild collapsible defaultOpen>
 *     <FindingCard severity="high" />
 *   </DataContainerCard>
 * </DataContainerCard>
 *
 * // With visual styling
 * <DataContainerCard
 *   title="Summary"
 *   borderRadius="md"
 *   shadow="md"
 *   showBorder
 * >
 *   <ResultsSummary />
 * </DataContainerCard>
 * ```
 */
export const DataContainerCard: React.FC<DataContainerCardProps> = ({
  title,
  customTitleComponent,
  children,
  collapsible = false,
  defaultOpen = true,
  open,
  onOpenChange,
  isChild = false,
  rightSection,
  borderRadius = 'none',
  shadow = 'none',
  borderColor,
  showBorder = false,
  showHeaderBorderBottom = true,
  headerHeight,
  stickyHeader = false,
  contentPadding = 'md',
  id,
  dataId,
  dataTestId,
  className,
  style,
}) => {
  // Use controlled state if `open` prop is provided, otherwise use internal state
  const isControlled = open !== undefined;
  const [internalOpen, setInternalOpen] = useState(defaultOpen);

  const isOpen = isControlled ? open : internalOpen;

  useEffect(() => {
    if (!isControlled) {
      setInternalOpen(defaultOpen);
    }
  }, [isControlled, defaultOpen]);

  const handleToggle = useCallback(
    (e: React.MouseEvent | React.KeyboardEvent) => {
      if (!collapsible) return;

      e.stopPropagation();
      e.preventDefault();

      const newOpenState = !isOpen;

      if (!isControlled) {
        setInternalOpen(newOpenState);
      }

      if (onOpenChange) {
        onOpenChange(newOpenState);
      }
    },
    [collapsible, isOpen, isControlled, onOpenChange]
  );

  const renderTitle = () => {
    if (customTitleComponent) {
      return customTitleComponent;
    }

    if (title) {
      return isChild ? (
        <NestedHeaderTitle>{title}</NestedHeaderTitle>
      ) : (
        <HeaderTitle>{title}</HeaderTitle>
      );
    }

    return null;
  };

  const hasContent = !!(
    (typeof children === 'string' && children.trim().length > 0) ||
    (Array.isArray(children) && children.length > 0) ||
    React.isValidElement(children)
  );

  // For non-collapsible mode, always show content
  const showContent = collapsible ? isOpen : true;

  const headerExpandLessId = `datacontainercard-header-expand-less-${id || dataId || 'header-expand-less'}`;
  const headerExpandMoreId = `datacontainercard-header-expand-more-${id || dataId || 'header-expand-more'}`;
  const contentId = `datacontainercard-content-${id || dataId || 'content'}`;

  return (
    <Container
      $borderRadius={borderRadius}
      $shadow={shadow}
      $showBorder={showBorder}
      $borderColor={borderColor}
      $isChild={isChild}
      $isOpen={showContent}
      $collapsible={collapsible}
      data-id={dataId}
      data-testid={dataTestId}
      className={className}
      style={style}
    >
      <Header
        $isOpen={showContent}
        $isChild={isChild}
        $height={headerHeight}
        $showHeaderBorderBottom={showHeaderBorderBottom}
        $stickyHeader={stickyHeader}
        $collapsible={collapsible}
        onClick={collapsible ? handleToggle : undefined}
        data-id={dataId ? `${dataId}-header` : undefined}
      >
        <LeftSection>
          {collapsible && (
            <>
              {isOpen ? (
                <Button
                  id={headerExpandLessId}
                  aria-label="Collapse section button"
                  aria-expanded={isOpen}
                  aria-controls={contentId}
                  icon={<Icon name="keyboard_arrow_up" size="md" />}
                  variant="ghost"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleToggle(e as unknown as React.MouseEvent);
                  }}
                />
              ) : (
                <Button
                  id={headerExpandMoreId}
                  aria-label="Expand section button"
                  aria-expanded={isOpen}
                  aria-controls={contentId}
                  icon={<Icon name="keyboard_arrow_down" size="md" />}
                  variant="ghost"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleToggle(e as unknown as React.MouseEvent);
                  }}
                />
              )}
            </>
          )}

          {renderTitle()}
        </LeftSection>
        {rightSection && <RightSection>{rightSection}</RightSection>}
      </Header>

      {hasContent && (
        <Content
          $isOpen={showContent}
          $isChild={isChild}
          $hasContent={hasContent}
          $contentPadding={contentPadding}
          $collapsible={collapsible}
          role="region"
          aria-labelledby={isOpen ? headerExpandLessId : headerExpandMoreId}
          id={contentId}
        >
          {showContent && children}
        </Content>
      )}
    </Container>
  );
};

DataContainerCard.displayName = 'DataContainerCard';

export default DataContainerCard;
